import React, { useState, useEffect, useCallback } from 'react';

const WinePairingCard = ({ question, allPairings, onAnswer }) => {
  const [options, setOptions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [explanation, setExplanation] = useState('');

  // Shuffle array function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Generate distractors
  const generateDistractors = useCallback((correctPairing, allPairings) => {
    const distractors = allPairings.filter(pairing => pairing.dish !== correctPairing.dish);
    const shuffledDistractors = shuffleArray(distractors);
    return shuffledDistractors.slice(0, 3);
  }, []);

  // Setup question when component mounts or question changes
  useEffect(() => {
    if (!question) return;

    // Reset state
    setSelectedAnswer(null);
    setIsAnswered(false);

    // Set correct answer and explanation
    setCorrectAnswer(question.wineType);
    setExplanation(question.explanation);

    // Generate options (correct wine + 3 distractors)
    const distractors = generateDistractors(question, allPairings);
    const allOptions = [
      question.wineType,
      ...distractors.map(pairing => pairing.wineType)
    ];

    setOptions(shuffleArray(allOptions));
  }, [question, allPairings, generateDistractors]);

  // Handle answer selection
  const handleAnswerClick = (answer) => {
    if (isAnswered) return;

    setSelectedAnswer(answer);
    setIsAnswered(true);
    
    const isCorrect = answer === correctAnswer;
    onAnswer(isCorrect, answer, correctAnswer);
  };

  // Get button class based on state
  const getButtonClass = (option) => {
    if (!isAnswered) return 'answer-button';
    
    let classes = 'answer-button disabled';
    
    if (option === correctAnswer) {
      classes += ' correct';
    } else if (option === selectedAnswer && option !== correctAnswer) {
      classes += ' incorrect';
    }
    
    return classes;
  };

  // Generate Google search URL
  const getSearchUrl = () => {
    return `https://google.com/search?q=${encodeURIComponent(`${question.dish} wine pairing ${question.wineType}`)}`;
  };

  if (!question) return null;

  return (
    <div className="question-card">
      <div className="question">
        <strong>What wine pairs best with:</strong>
        <br /><br />
        <div className="wine-pairing-dish">
          "{question.dish}"
        </div>
        <br />
        {isAnswered && (
          <div className="wine-explanation">
            <strong>Why this pairing works:</strong>
            <p>{explanation}</p>
            {question.whyItWorks && (
              <p><em>{question.whyItWorks}</em></p>
            )}
          </div>
        )}
      </div>
      
      <div className="answers">
        {options.map((option, index) => (
          <button
            key={index}
            className={getButtonClass(option)}
            onClick={() => handleAnswerClick(option)}
            disabled={isAnswered}
          >
            {option}
          </button>
        ))}
      </div>
      
      {isAnswered && question.specificRecommendations && (
        <div className="wine-recommendations">
          <strong>Specific Recommendations:</strong>
          <ul>
            {question.specificRecommendations.map((rec, index) => (
              <li key={index}>{rec}</li>
            ))}
          </ul>
        </div>
      )}
      
      <a 
        href={getSearchUrl()} 
        target="_blank" 
        rel="noopener noreferrer"
        className="lookup-link"
      >
        🔍 Look Up Wine Pairing
      </a>
    </div>
  );
};

export default WinePairingCard; 